<template>
  <!-- 
     TODO: This page doesn't yet support polling, because it's an expensive query
     to get all the activity info. I think polling requires (1) some kind of 
     ActivityCache table to make the activity query cheap, and (2) updating our
     actionCountsPoller.ts to handle activities, so any page that shows activities
     can refresh without a dedicated query.
  -->
  <PageChrome :show-header="false" tight>
    <div class="accountsPage">
      <div v-if="error" class="pageError">{{ error }}</div>

      <ListToolbar
        noun="accounts"
        hide-select
        :page-size="items.length"
        :total="total"
        :range-label="rangeLabel"
        :sort="filters.sort"
        :sort-options="SORT_OPTIONS"
        :can-prev="filters.page > 1"
        :can-next="filters.page < pageCount"
        @prev="goToPage(filters.page - 1)"
        @next="goToPage(filters.page + 1)"
        @update:sort="(v: string) => setFilters({ sort: v })"
      >
        <template #filters>
          <input
            ref="searchInputEl"
            v-model="searchText"
            type="text"
            class="searchInput"
            placeholder="Search by name or domain…"
            @input="onSearchInput"
          />
          <div style="width: 200px">
            <SearchSelect
              :modelValue="filters.play"
              :options="playOptions"
              placeholder="All plays"
              @update:modelValue="(v: string) => setFilters({ play: v })"
            />
          </div>
          <div style="width: 150px">
            <SearchSelect
              :modelValue="filters.engagement"
              :options="tristateOptions('Engagement')"
              placeholder="Engagement"
              @update:modelValue="(v: string) => setFilters({ engagement: v })"
            />
          </div>
          <div style="width: 150px">
            <SearchSelect
              :modelValue="filters.completed_actions"
              :options="tristateOptions('Completed')"
              placeholder="Completed"
              @update:modelValue="(v: string) => setFilters({ completed_actions: v })"
            />
          </div>
          <div style="width: 150px">
            <SearchSelect
              :modelValue="filters.pending_actions"
              :options="tristateOptions('Pending')"
              placeholder="Pending"
              @update:modelValue="(v: string) => setFilters({ pending_actions: v })"
            />
          </div>
        </template>
      </ListToolbar>

      <!-- Column headers -->
      <div class="listHead accountGrid">
        <span class="colLabel">Account</span>
        <span class="colLabel">Runs</span>
        <span class="colLabel">Actions (60d)</span>
        <span class="colLabel">Engagement (60d)</span>
        <span class="colLabel">Timeline (60d)</span>
        <span class="colLabel">Plays</span>
      </div>

      <div v-if="loading && items.length === 0" class="empty">
        <span class="emptyText">Loading accounts…</span>
      </div>
      <div v-else-if="items.length === 0" class="empty">
        <span class="emptyText">No accounts found.</span>
      </div>
      <div v-else class="memberList">
        <button
          v-for="item in items"
          :key="item.id"
          type="button"
          class="memberRow accountGrid"
          :class="{ memberRowSelected: selectedId === item.id }"
          :data-row-id="item.id"
          @click="selectAccount(item)"
        >
          <!-- Account identity -->
          <div class="cellAccount">
            <HeaderWithIcon :domain="item.domain" variant="stacked">
              <template #primary>
                <span class="primaryName">{{ item.name || item.domain }}</span>
              </template>
              <template #secondary>
                <span>{{ item.domain }}</span>
                <span v-if="item.contact_count > 0" class="contactCount">· {{ item.contact_count }} contact{{ item.contact_count === 1 ? '' : 's' }}</span>
              </template>
            </HeaderWithIcon>
          </div>

          <!-- Runs: most recent agent run per play -->
          <div class="cellRuns">
            <template v-for="play in item.plays" :key="play.play_id">
              <div v-if="play.last_run_id && play.last_run_status" class="runEntry">
                <router-link
                  :to="`/agent-runs/${play.last_run_id}`"
                  class="runLink"
                  @click.stop
                >
                  <AgentRunStatusPill :status="play.last_run_status" />
                </router-link>
                <span class="runDate">{{ formatRelative(play.last_run_at) }}</span>
              </div>
            </template>
            <span v-if="!item.plays.some(p => p.last_run_id)" class="mutedDash">—</span>
          </div>

          <!-- Actions: outbound reachout pills -->
          <div class="cellActions">
            <div v-if="item.outbound_pills.length > 0" class="pillRow">
              <ReachoutPill
                v-for="pill in item.outbound_pills"
                :key="pill.id"
                :reachout="pill"
              />
              <span v-if="item.outbound_count > item.outbound_pills.length" class="n-pill overflowPill">+{{ item.outbound_count - item.outbound_pills.length }}</span>
            </div>
            <span v-if="item.outbound_pills.length === 0" class="mutedDash">—</span>
          </div>

          <!-- Engagement: engaged pills -->
          <div class="cellEngagement">
            <ActivityPillList v-if="item.engaged_pills.length > 0" :pills="item.engaged_pills" :on-updated="() => load()" />
            <span v-if="item.engaged_count > item.engaged_pills.length" class="n-pill overflowPill">+{{ item.engaged_count - item.engaged_pills.length }}</span>
            <span v-if="item.engaged_pills.length === 0" class="mutedDash">—</span>
          </div>

          <!-- Timeline sparkline -->
          <div class="cellTimeline">
            <ActivitySparkline :timeline="item.activity_timeline" />
          </div>

          <!-- Plays: play name pills with SPA state -->
          <div class="cellPlays">
            <router-link
              v-for="play in item.plays"
              :key="play.play_id"
              :to="`/sales-plays/${play.play_id}/candidates/companies`"
              class="n-pill playPill"
              @click.stop
            >
              <span class="n-pillDot" :class="playStateDot(play.state)" />
              <span class="playPillText">{{ play.play_name }}</span>
              <span class="playState">{{ play.state }}</span>
            </router-link>
            <span v-if="item.plays.length === 0" class="mutedDash">—</span>
          </div>
        </button>
      </div>
    </div>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import PageChrome from '../components/PageChrome.vue'
import HeaderWithIcon from '../../components/HeaderWithIcon.vue'
import ListToolbar from '../components/list/ListToolbar.vue'
import SearchSelect from '../../components/SearchSelect.vue'
import ReachoutPill from '../components/ReachoutPill.vue'
import AgentRunStatusPill from '../../components/AgentRunStatusPill.vue'
import AppIcon from '../../components/AppIcon.vue'
import ActivityPillList from '../components/ActivityPillList.vue'
import ActivitySparkline from '../components/ActivitySparkline.vue'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { DRAWER_CONTEXT_ACCOUNT } from '../../drawer/drawerTypes'
import { apiFetch } from '../../services/api'
import {
  useAccountsQuery,
  SORT_OPTIONS,
  type EnrichedAccountItem,
} from '../composables/useAccountsQuery'

const {
  items, total, loading, error, filters, pageCount, rangeLabel,
  cacheIsUsable, load, setFilters, goToPage,
} = useAccountsQuery()

const { drawerContext, setContext, openDrawer } = useContextDrawer()

// ── Filter bar local state ──
const searchText = ref(filters.value.q)
const searchInputEl = ref<HTMLInputElement | null>(null)
let searchTimeout: ReturnType<typeof setTimeout> | null = null

const playOptions = ref<{ value: string; label: string }[]>([{ value: '', label: 'All plays' }])

async function loadFilterOptions() {
  try {
    const plays = await apiFetch('/sales-plays').catch(() => null)
    const playRows = Array.isArray(plays) ? plays : (plays?.sales_plays || plays?.plays || [])
    playOptions.value = [
      { value: '', label: 'All plays' },
      ...playRows.map((p: any) => ({ value: String(p.id), label: String(p.name || p.id) })),
    ]
  } catch { /* non-critical */ }
}

function tristateOptions(label: string) {
  return [
    { value: '', label: `Any ${label.toLowerCase()}` },
    { value: 'yes', label: `Has ${label.toLowerCase()}` },
    { value: 'no', label: `No ${label.toLowerCase()}` },
  ]
}

function onSearchInput() {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    setFilters({ q: searchText.value.trim() })
  }, 300)
}

// Keep local search in sync with URL
watch(() => filters.value.q, (v) => { searchText.value = v })

// ── Drawer selection ──
const selectedId = computed(() => {
  const ctx = drawerContext.value
  if (ctx?.type === 'account') return ctx.accountId
  return null
})

function selectAccount(item: EnrichedAccountItem) {
  setContext({ type: DRAWER_CONTEXT_ACCOUNT, accountId: item.id })
  openDrawer()
}

// ── Arrow key navigation ──
function onWindowKeydown(event: KeyboardEvent) {
  if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return
  const target = event.target instanceof Element ? event.target : null
  if (target && target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"]')) return

  if (event.key === 'ArrowDown' || event.key === 'j') {
    const currentId = selectedId.value
    if (!currentId) {
      if (items.value.length > 0) selectAccount(items.value[0])
      return
    }
    const idx = items.value.findIndex(i => i.id === currentId)
    if (idx < 0) return
    const next = items.value[idx + 1]
    if (next) {
      event.preventDefault()
      selectAccount(next)
      void scrollRowIntoView(next.id)
    }
  } else if (event.key === 'ArrowUp' || event.key === 'k') {
    const currentId = selectedId.value
    if (!currentId) return
    const idx = items.value.findIndex(i => i.id === currentId)
    if (idx <= 0) return
    const prev = items.value[idx - 1]
    event.preventDefault()
    selectAccount(prev)
    void scrollRowIntoView(prev.id)
  }
}

async function scrollRowIntoView(id: string) {
  await nextTick()
  const el = document.querySelector(`[data-row-id="${id}"]`) as HTMLElement | null
  if (!el) return
  try { el.scrollIntoView({ block: 'nearest' }) } catch { /* */ }
}

// ── Helpers ──
function playStateDot(state: string): string {
  if (state === 'active') return 'dotActive'
  if (state === 'qualified') return 'dotQualified'
  if (state === 'staged') return 'dotStaged'
  if (state === 'engaged') return 'dotEngaged'
  if (state === 'disqualified' || state === 'timed_out') return 'dotTerminal'
  return 'dotDefault'
}

function formatRelative(isoDate: string): string {
  const d = new Date(isoDate)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// ── Lifecycle ──
let initialLoadDone = false
watch(filters, () => {
  if (initialLoadDone) void load()
}, { deep: true })

onMounted(async () => {
  if (!cacheIsUsable()) await load()
  initialLoadDone = true
  loadFilterOptions()
  window.addEventListener('keydown', onWindowKeydown)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onWindowKeydown)
  if (searchTimeout) clearTimeout(searchTimeout)
})
</script>

<style scoped>
.accountsPage {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.pageError {
  padding: 8px 16px;
  font-size: 13px;
  color: var(--color-danger, #dc2626);
  background: var(--color-danger-soft, #fef2f2);
  border-bottom: 1px solid var(--color-border);
}

.searchInput {
  width: 260px;
  padding: 6px 12px;
  font-size: 13px;
  border: 1px solid var(--color-border, #e5e2dc);
  border-radius: var(--radius-md, 8px);
  background: var(--color-bg-surface, #fff);
  color: var(--color-text-primary);
  outline: none;
  transition: border-color 150ms ease;
}
.searchInput:focus {
  border-color: var(--color-accent, #059669);
}

/* ── Grid ────────────────────────────────────────────────────────── */
.accountGrid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) 160px minmax(0, 1.2fr) minmax(0, 0.8fr) 130px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
}

.listHead {
  padding: 6px 12px;
  border-bottom: 1px solid var(--color-border, #e5e2dc);
}

.colLabel {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary, #a3a3a3);
}

.memberList {
  display: flex;
  flex-direction: column;
}

.memberRow {
  padding: 8px 12px;
  border-top: 1px solid var(--color-border, #e5e2dc);
  background: transparent;
  border-left: 0;
  border-right: 0;
  border-bottom: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  outline: none;
  transition: background 100ms ease;
}
.memberRow:first-child {
  border-top: 0;
}
.memberRowSelected,
.memberRowSelected:hover {
  background: var(--color-bg-subtle, #f2ede5);
}

/* ── Account cell ── */
.cellAccount {
  min-width: 0;
}
.primaryName {
  font-weight: 700;
  color: var(--color-text-primary, var(--text));
}
.contactCount {
  margin-left: 4px;
  color: var(--color-text-tertiary, #a3a3a3);
}

/* ── Runs cell ── */
.cellRuns {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.runEntry {
  display: flex;
  align-items: center;
  gap: 6px;
}
.runLink {
  text-decoration: none;
  flex-shrink: 0;
}
.runLink:hover { opacity: 0.75; }
.runDate {
  font-size: 11px;
  color: var(--color-text-tertiary, #a3a3a3);
  white-space: nowrap;
}

/* ── Timeline cell ── */
.cellTimeline {
  display: flex;
  align-items: center;
}

/* ── Actions cell ── */
.cellActions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.pillRow {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.overflowPill {
  align-self: flex-start;
  color: var(--color-text-tertiary, #a3a3a3);
  cursor: default;
}

/* ── Engagement cell ── */
.cellEngagement {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.engagedBadge {
  font-size: 11px;
  font-weight: 600;
  color: var(--color-accent, #059669);
}

/* ── Plays cell ── */
.cellPlays {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
}
.playPill {
  align-self: start;
  max-width: 100%;
  text-decoration: none;
  color: inherit;
}
.playPill:hover { border-color: var(--color-accent); }
.playPillText { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; flex-shrink: 1; }
.playState { font-size: 10px; color: var(--color-text-tertiary, #a3a3a3); white-space: nowrap; flex-shrink: 0; }
.dotActive { background: var(--color-accent, #059669); }
.dotQualified { background: var(--color-info, #3b82f6); }
.dotStaged { background: var(--color-text-tertiary, #a3a3a3); }
.dotEngaged { background: var(--color-accent, #059669); }
.dotTerminal { background: var(--color-danger, #dc2626); }
.dotDefault { background: var(--color-text-tertiary, #a3a3a3); }
.mutedDash {
  font-size: 12px;
  color: var(--color-text-tertiary, #a3a3a3);
}

.empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-8, 56px);
}
.emptyText {
  font-size: 14px;
  color: var(--color-text-tertiary, #a3a3a3);
}
</style>
